import { Injectable, Inject } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { SharedLibConfig } from '../../config/shared-lib-config';
import { SHARED_LIB_CONFIG } from '../../config/shared-lib-config.token';
import { CompanyGroupDto, Sgkz010aRequest, SGKZ010AService } from 'sg-api';
import { ApiCommonService } from '../api-common.service';
import { catchError, map, timeout } from 'rxjs/operators';
import { LoginUserIdService } from '../login-user-id.service';
/**
 * トークンベースの認証サービス
 * @export
 * @class AuthService
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {

  /** 二重呼び出し防止フラグ */
  // 権限情報取得の呼び出しは一度のみとする。
  public isCalledFlag = false;

  /** 画面表示許可リスト */
  private screenViewAllowList: string[] = [];

  /** 画面項目活性許可リスト */
  private screenActivateAllowList: string[] = [];

  /** 会社取扱可能リスト */
  private companyAvailableList: string[] = [];

  /**
   * コンストラクタ
   * @memberof AuthService
   */
  constructor(
    private oauthService: OAuthService,
    private sgkz010AService: SGKZ010AService,
    private apiCommonService: ApiCommonService,
    private loginUserIdService: LoginUserIdService,
    @Inject(SHARED_LIB_CONFIG) private sharedLibConfig: SharedLibConfig
  ) {
  }

  /**
   * URLのクエリを取得し、ログインを実施する.
   * 認証サービスからのリダイレクトであればクエリを取得でき、
   * それを用いてトークン取得のリクエストを実行する。
   *
   * @return {*}  {Observable<boolean>}
   * @memberof AuthService
   */
  tryLogin(): Observable<boolean> {
    this.oauthService.configure(this.sharedLibConfig.authCodeFlowConfig);
    return from(this.oauthService.loadDiscoveryDocumentAndTryLogin());
  }

  /**
   * 認証サービスのログイン画面に遷移する
   *
   * @memberof AuthService
   */
  transition2Login(): void {
    this.oauthService.initCodeFlow();
  }

  /**
   * ログアウト
   *
   * @memberof AuthService
   */
  logout(logoutFlg: boolean): void {
    if (logoutFlg) {
      this.oauthService.logOut(logoutFlg);
    } else {
      this.oauthService.logOut(
        {
          client_id: this.oauthService.clientId,
          redirect_uri: this.oauthService.redirectUri,
          response_type: this.oauthService.responseType
        }
      );
    }
  }

  /**
   * ログイン状態判定
   *
   * @return {*}  {Observable<boolean>}
   * @memberof AuthService
   */
  isAuthenticated(): boolean {
    return (this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken());
  }

  /**
   * リフレッシュトークンを使ったトークン自動更新の起動
   *
   * @memberof AuthService
   */
  autoUpdateToken(): void {
    this.oauthService.setupAutomaticSilentRefresh();
  }

  /**
   * idトークン取得
   *
   * @return {*}  {Observable<string>}
   * @memberof AuthService
   */
  getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  /**
   * ロール情報を取得する。
   * ※ロール情報取得は同期処理とする。
   *
   * @memberof UsedSgkz010aService
   */
  async getRolesInfo(): Promise<void> {

    // 二重呼び出し防止
    this.isCalledFlag = true;

    // ユーザID更新
    let userId = '';
    this.loginUserIdService.loginUserId.subscribe(loginUserId => {
      userId = loginUserId;
    });

    // リクエスト情報生成
    let request: Sgkz010aRequest = {
      requestCommon: {},
      userId: userId
    };

    await this.sgkz010AService.kz10RSGKZ010APost(this.apiCommonService.setRequestCommon(<any>request), 'response').pipe(
      map(response => {
        // エラーが設定されていないかチェックする
        this.apiCommonService.checkError(response);

        for (const userAuthority of response.body.permition) {
          let action: string = userAuthority.action;
          if (action.startsWith('1')) {
            this.screenViewAllowList.push(action);
          }
          if (action.startsWith('2')) {
            this.screenActivateAllowList.push(action);
          }
          if (action.startsWith('3')) {
            this.companyAvailableList.push(action);
          }
        }
      }),
      timeout(Number(this.sharedLibConfig.httpTimeout)),
      catchError((error) => {
        return throwError(error).toPromise();
      })
    )
      .toPromise();
  }


  /**
   * ページ権限許可判定
   * @param {string} screenId
   * @return {*}  {boolean}
   * @memberof AuthService
   */
  async isPageRolesAllowed(screenId: string): Promise<boolean> {
    // 権限取得は同期処理とする
    // ※権限未取得のまま、画面描画を行わないため
    try {
      if (!this.isCalledFlag) {
        await this.getRolesInfo();
      }
    } catch (error) {
      return throwError(error).toPromise();
    }

    const searchScreenId = '1_' + screenId + '_page';
    const screenViewAllowList = this.screenViewAllowList;

    return screenViewAllowList.includes(searchScreenId);
  }

  /**
   * 会社ロール取得
   * @return {*}  {CompanyGroupDto[]}
   * @memberof AuthService
   */
  getCompanyRolesAllowed(): CompanyGroupDto[] {

    // 昇順にソート
    const sortCompanyAvailableList = this.companyAvailableList.sort();
    // 8社ロール情報生成
    let companyGroupDtoList: CompanyGroupDto[] = [];

    for (const companyAvailableCode of sortCompanyAvailableList) {
      // 各項目定義
      let splitList = companyAvailableCode.split('_');
      let companyGroupCode = splitList[1];
      let companyCode = splitList[2];
      let companyCodeList: string[] = [];

      // 存在チェック
      let isCodeExist = companyGroupCode !== undefined && companyCode !== undefined;
      let companyGroupDto = companyGroupDtoList.find(dto => dto.companyGroupCode === companyGroupCode);

      if (isCodeExist && companyGroupDto !== undefined) {
        // 会社コードを会社コードリストに追加する。
        companyCodeList = companyGroupDto.companyCodeList;
        companyCodeList.push(companyCode);
        companyGroupDto = {
          companyGroupCode,
          companyCodeList
        }

        let index = companyGroupDtoList.findIndex(dto => dto.companyGroupCode === companyGroupCode);
        companyGroupDtoList.splice(index, 1, companyGroupDto);
        continue;

      } else if (isCodeExist && companyGroupDto === undefined) {
        // 新しい会社コードリストを生成する。
        companyCodeList.push(companyCode);
        companyGroupDto = {
          companyGroupCode,
          companyCodeList
        }

        companyGroupDtoList.push(companyGroupDto);
      }
    }

    return companyGroupDtoList;
  }

  /**
   * 画面項目表示許可判定
   * @param {string} screenId
   * @param {string} itemid
   * @return {*}  {boolean}
   * @memberof AuthService
   */
  isRolesDisplayAllowed(screenId: string, itemid: string): boolean {
    // 権限はisPageRolesAllowedで取得済みの状態で動作する
    const screenItemAllowedId = '1_' + screenId + '_' + itemid;
    return this.screenViewAllowList.includes(screenItemAllowedId);
  }

  /**
   * 画面項目非活性判定
   * @param {string} screenId
   * @param {string} itemId
   * @return {*}  {boolean}
   * @memberof AuthService
   */
  isRolesActivationDenied(screenId: string, itemId: string): boolean {
    // 権限はisPageRolesAllowedで取得済みの状態で動作する
    const screenActivateAllowList = '2_' + screenId + '_' + itemId;
    return !this.screenActivateAllowList.includes(screenActivateAllowList);
  }
}
